import { useState } from "react";
import { session, useSession } from "../../game/session";
import { useGameUi } from "../../game/store";
import { t } from "../../i18n";
import { apiErrorLabel } from "../labels";
import { Icon } from "../pixel";
import { DANGER_INK, Key, SectionTitle } from "../primitives";

/**
 * Le compte, dans le panneau Station : l'adresse connectée, la déconnexion, et à part, la suppression
 * définitive (deux appuis). La connexion elle-même se fait sur l'écran d'accueil.
 */
export function AccountSection() {
  const player = useSession((state) => state.player);
  const toast = useGameUi((ui) => ui.toast);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!player?.email) return null;

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (failure) {
      setError(apiErrorLabel(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label={t("account.title")}>
      <SectionTitle>{t("account.title")}</SectionTitle>
      <p className="text-[0.875rem] text-dim">{t("account.signedInAs")}</p>
      <p className="break-all text-base font-semibold text-ink">{player.email}</p>
      <Key className="mt-3" onClick={() => void run(() => session.logout())} disabled={busy}>
        <Icon name="exit" />
        {t("account.logout")}
      </Key>
      <p className="mt-1.5 text-[0.875rem] text-muted">{t("account.logoutHint")}</p>

      <div className="dotted mt-6 bg-top pt-4">
        {confirmDelete ? (
          <div className="flex flex-col gap-2">
            <p className="text-[0.9375rem] text-ink">{t("account.deleteWarning")}</p>
            <div className="flex flex-wrap gap-2">
              <Key
                tone="danger"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await session.deleteAccount();
                    toast(t("account.deleted"), "info");
                  })
                }
              >
                <Icon name="trash" />
                {t("account.deleteConfirm")}
              </Key>
              <Key tone="quiet" size="sm" onClick={() => setConfirmDelete(false)} disabled={busy}>
                {t("account.cancel")}
              </Key>
            </div>
          </div>
        ) : (
          <Key tone="quiet" size="sm" style={DANGER_INK} onClick={() => setConfirmDelete(true)}>
            <Icon name="trash" />
            {t("account.delete")}
          </Key>
        )}
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-[0.9375rem] text-stop">
          {error}
        </p>
      ) : null}
    </section>
  );
}
